import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  CalendarDays,
  CircleCheckBig,
  Clock,
  CircleOff,
  TreePalm,
  Check,
  X,
  FileSpreadsheet,
  Loader2,
  Pencil,
  Trash2,
  Search,
} from "lucide-react";
import { toast } from "react-hot-toast";
import * as helperService from "../../../services/helperService";
import { employeeService } from "../../../services/employeeService";
import { vacationTypeOptions } from "../../../constants/constants";

const statusStyles = {
  Pending: "bg-yellow-100 text-yellow-700",
  Approved: "bg-green-100 text-green-700",
  Rejected: "bg-red-100 text-red-700",
};

const VacationsOverview = ({ currentEmployee }) => {
  const [vacations, setVacations] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [activeFilter, setActiveFilter] = useState({ key: null, value: null });
  const [searchTerm, setSearchTerm] = useState("");
  const [rejectingVacationId, setRejectingVacationId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [editingVacation, setEditingVacation] = useState(null);
  const [deletingVacationId, setDeletingVacationId] = useState(null);

  const isHRManager = currentEmployee?.is_hr_manager === "Yes";

  // Capture action params from email deep-link on first render, then clear the URL
  const pendingActionRef = useRef(null);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const action = params.get("action");
    const vrId = params.get("vr_id");
    if (action && vrId) {
      pendingActionRef.current = { action, vrId };
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const loadVacations = useCallback(async () => {
    if (!currentEmployee) return;
    setLoading(true);
    try {
      const res = await employeeService.getEmployees("", 200, 0);
      const employees = res.items || [];
      setAllEmployees(employees);

      const targets = isHRManager
        ? employees
        : employees.filter(
            (item) =>
              String(item.employee?.manager_id) === String(currentEmployee.id),
          );

      const batches = await Promise.all(
        targets.map(async (item) => {
          const vacs = await employeeService.getVacations(item.id);
          return vacs.map((v) => ({
            ...v,
            employeeName: item.employee?.name ?? "",
          }));
        }),
      );

      setVacations(batches.flat());
    } catch (err) {
      console.error("Failed to load vacations", err);
      toast.error("Failed to load vacation requests");
    } finally {
      setLoading(false);
    }
  }, [currentEmployee, isHRManager]);

  useEffect(() => {
    loadVacations();
  }, [loadVacations]);

  // After vacations load, execute any pending action from an email deep-link
  useEffect(() => {
    if (loading || !pendingActionRef.current) return;
    const { action, vrId } = pendingActionRef.current;
    pendingActionRef.current = null;
    const vacation = vacations.find((v) => String(v.id) === String(vrId));
    if (!vacation || vacation.status !== "Pending") return;
    if (action === "approve") {
      handleApprove(vacation);
    } else if (action === "reject") {
      setRejectingVacationId(vacation.id);
      setRejectReason("");
    }
  }, [loading, vacations]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleApprove = async (vacation) => {
    try {
      await employeeService.updateVacation(vacation.employee_id, vacation.id, {
        type: vacation.type,
        start: vacation.start,
        end: vacation.end,
        days: vacation.days,
        status: "Approved",
        approved_by: currentEmployee.id,
        notes: vacation.notes,
      });
      toast.success("Request approved");
      await loadVacations();
    } catch {
      toast.error("Failed to approve request");
    }
  };

  const handleRejectConfirm = async (vacation) => {
    try {
      await employeeService.updateVacation(vacation.employee_id, vacation.id, {
        type: vacation.type,
        start: vacation.start,
        end: vacation.end,
        days: vacation.days,
        status: "Rejected",
        approved_by: currentEmployee.id,
        notes: rejectReason.trim() || null,
      });
      setRejectingVacationId(null);
      setRejectReason("");
      toast.success("Request rejected");
      await loadVacations();
    } catch {
      toast.error("Failed to reject request");
    }
  };

  const handleEditSave = async () => {
    try {
      await employeeService.updateVacation(
        editingVacation.employee_id,
        editingVacation.id,
        {
          type: editingVacation.type,
          start: editingVacation.start,
          end: editingVacation.end,
          days: Number(editingVacation.days),
          status: editingVacation.status,
          approved_by: editingVacation.approved_by ?? currentEmployee.id,
          notes: editingVacation.notes || null,
        },
      );
      setEditingVacation(null);
      toast.success("Request updated");
      await loadVacations();
    } catch {
      toast.error("Failed to update request");
    }
  };

  const handleDeleteConfirm = async (vacation) => {
    try {
      await employeeService.deleteRequest(vacation.employee_id, vacation.id);
      setDeletingVacationId(null);
      toast.success("Request deleted");
      await loadVacations();
    } catch {
      toast.error("Failed to delete request");
    }
  };

  const stats = useMemo(
    () => [
      {
        label: "Total Requests",
        value: vacations.length,
        helper: "All requests",
        icon: CalendarDays,
        helperClass: "text-canopy-blue",
        iconClass: "text-canopy-blue",
        filterKey: null,
        filterValue: null,
      },
      {
        label: "Pending",
        value: vacations.filter((v) => v.status === "Pending").length,
        helper: "Pending requests",
        icon: Clock,
        helperClass: "text-yellow-600",
        iconClass: "text-yellow-600",
        filterKey: "status",
        filterValue: "Pending",
      },
      {
        label: "Approved",
        value: vacations.filter((v) => v.status === "Approved").length,
        helper: "Approved requests",
        icon: CircleCheckBig,
        helperClass: "text-green-600",
        iconClass: "text-green-600",
        filterKey: "status",
        filterValue: "Approved",
      },
      {
        label: "Rejected",
        value: vacations.filter((v) => v.status === "Rejected").length,
        helper: "Rejected requests",
        icon: CircleOff,
        helperClass: "text-red-600",
        iconClass: "text-red-600",
        filterKey: "status",
        filterValue: "Rejected",
      },
    ],
    [vacations],
  );

  const filteredVacations = useMemo(() => {
    let result = vacations;
    if (activeFilter.key) {
      result = result.filter((v) => v[activeFilter.key] === activeFilter.value);
    }
    const term = searchTerm.trim().toLowerCase();
    if (term) {
      result = result.filter((v) =>
        [v.employeeName, v.type, v.status, v.notes, v.approver_name]
          .filter(Boolean)
          .some((field) => field.toLowerCase().includes(term)),
      );
    }
    return result;
  }, [vacations, activeFilter, searchTerm]);

  const handleStatClick = (filterKey, filterValue) => {
    if (
      !filterKey ||
      (activeFilter.key === filterKey && activeFilter.value === filterValue)
    ) {
      setActiveFilter({ key: null, value: null });
    } else {
      setActiveFilter({ key: filterKey, value: filterValue });
    }
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      let blob;
      if (isHRManager) {
        blob = await employeeService.exportVacations({});
      } else {
        // BFS to collect all direct + indirect report IDs
        const subtreeIds = new Set();
        const queue = [currentEmployee.id];
        while (queue.length > 0) {
          const parentId = queue.shift();
          for (const item of allEmployees) {
            if (
              String(item.employee?.manager_id) === String(parentId) &&
              !subtreeIds.has(item.id)
            ) {
              subtreeIds.add(item.id);
              queue.push(item.id);
            }
          }
        }
        blob = await employeeService.exportVacations({
          employeeIds: [...subtreeIds],
        });
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `vacation_requests_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export ready");
    } catch (err) {
      console.error("Export failed", err);
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Loading vacation requests...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      <section className="bg-white border border-canopy-border rounded-xl p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {stats.map((item) => {
            const Icon = item.icon;
            const isActive =
              item.filterKey === null
                ? activeFilter.key === null
                : activeFilter.key === item.filterKey &&
                  activeFilter.value === item.filterValue;
            return (
              <button
                key={item.label}
                type="button"
                onClick={() =>
                  handleStatClick(item.filterKey, item.filterValue)
                }
                className={`text-left border rounded-xl p-5 bg-white transition-colors ${
                  isActive
                    ? "border-canopy-blue ring-2 ring-canopy-blue/10"
                    : "border-canopy-border hover:border-canopy-blue/40"
                }`}
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-canopy-text">
                    {item.label}
                  </h3>
                  <Icon className={`h-4 w-4 ${item.iconClass}`} />
                </div>
                <p className="text-4xl font-bold text-canopy-text mt-3">
                  {item.value}
                </p>
                <p className={`text-sm mt-2 ${item.helperClass}`}>
                  {item.helper}
                </p>
              </button>
            );
          })}
        </div>
      </section>

      <section className="bg-white border border-canopy-border rounded-xl p-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <h2 className="text-4xl/[1.1] sm:text-4xl font-bold text-canopy-text">
            Requests
          </h2>

          <div className="flex flex-col md:flex-row gap-2 w-full md:w-auto">
            <div className="flex items-center border border-canopy-border rounded-lg px-3 py-2 w-full md:w-80">
              <Search className="h-4 w-4 text-canopy-textMuted mr-2" />
              <input
                type="text"
                placeholder="Search requests"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1 outline-none"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm("")}
                  className="ml-2 text-gray-400 hover:text-gray-600 focus:outline-none"
                >
                  ✕
                </button>
              )}
            </div>

            <button
              type="button"
              onClick={handleExportExcel}
              disabled={exporting || vacations.length === 0}
              className="px-3 py-1 text-xs inline-flex items-center disabled:opacity-60"
              title="Export vacation requests to Excel"
              aria-label="Export vacation requests to Excel"
            >
              {exporting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  Export to Excel
                </>
              )}
            </button>
          </div>
        </div>

        <div className="mt-6 space-y-4">
          {filteredVacations.length === 0 ? (
            <p className="text-gray-500 italic">No vacation requests found</p>
          ) : (
            filteredVacations.map((vacation) => (
              <div
                key={vacation.id}
                className="border border-canopy-border rounded-xl p-5 bg-white space-y-4"
              >
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 rounded-full bg-green-600 text-white flex items-center justify-center font-semibold">
                      <TreePalm className="h-4 w-4" />
                    </div>

                    <div>
                      <p className="text-2xl font-bold text-canopy-text">
                        {vacation.employeeName}
                      </p>
                      <p className="text-sm text-canopy-textMuted">
                        {vacation.type} • Days: {vacation.days}
                      </p>
                      <p className="text-sm text-canopy-textMuted mt-1">
                        {helperService.formatJoinedDate(vacation.start)} —{" "}
                        {helperService.formatJoinedDate(vacation.end)}
                      </p>
                      {vacation.approver_name && (
                        <p className="text-xs text-canopy-textMuted mt-1">
                          {vacation.status === "Rejected"
                            ? "Rejected"
                            : "Approved"}{" "}
                          by{" "}
                          <span className="font-semibold">
                            {vacation.approver_name}
                          </span>
                        </p>
                      )}
                      {vacation.status === "Rejected" && vacation.notes && (
                        <p className="text-xs text-red-600 mt-1">
                          Reason: {vacation.notes}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <span
                      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold uppercase ${
                        statusStyles[vacation.status] ??
                        "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {vacation.status}
                    </span>
                    {vacation.status === "Pending" && (
                      <>
                        <button
                          type="button"
                          onClick={() => handleApprove(vacation)}
                          className="px-3 py-1 text-xs font-semibold rounded-lg border border-canopy-border hover:bg-green-100 transition"
                          title="Approve"
                        >
                          <Check className="h-3 w-3" />
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setRejectingVacationId(vacation.id);
                            setRejectReason("");
                          }}
                          className="px-3 py-1 text-xs font-semibold rounded-lg border border-canopy-border hover:bg-red-100 transition"
                          title="Reject"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </>
                    )}
                    {isHRManager && (
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            setEditingVacation({ ...vacation });
                            setDeletingVacationId(null);
                            setRejectingVacationId(null);
                          }}
                          className="px-3 py-1 text-xs font-semibold rounded-lg border border-canopy-border hover:bg-blue-50 transition"
                          title="Edit"
                        >
                          <Pencil className="h-3 w-3" />
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setDeletingVacationId(vacation.id);
                            setEditingVacation(null);
                            setRejectingVacationId(null);
                          }}
                          className="px-3 py-1 text-xs font-semibold rounded-lg border border-canopy-border hover:bg-red-50 transition"
                          title="Delete"
                        >
                          <Trash2 className="h-3 w-3 text-red-500" />
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {rejectingVacationId === vacation.id && (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 space-y-2">
                    <label className="block text-sm font-medium text-orange-800">
                      Rejection Reason
                    </label>
                    <input
                      type="text"
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      placeholder="Enter reason (optional)"
                      className="w-full px-3 py-2 border border-orange-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                      autoFocus
                    />
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setRejectingVacationId(null);
                          setRejectReason("");
                        }}
                        className="px-3 py-1 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRejectConfirm(vacation)}
                        className="px-3 py-1 text-sm bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors"
                      >
                        Confirm Rejection
                      </button>
                    </div>
                  </div>
                )}

                {editingVacation?.id === vacation.id && (
                  <div className="border border-canopy-border rounded-xl p-5 bg-gray-50">
                    <div className="flex flex-wrap items-center gap-3 mb-4">
                      <h3 className="text-2xl font-bold text-canopy-text">
                        Edit Vacation Request
                      </h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-canopy-text mb-2">
                          Type
                        </label>
                        <select
                          value={editingVacation.type}
                          onChange={(e) =>
                            setEditingVacation((v) => ({
                              ...v,
                              type: e.target.value,
                            }))
                          }
                          className="w-full border border-canopy-border rounded-lg px-3 py-2 text-sm"
                        >
                          {vacationTypeOptions.map((t) => (
                            <option key={t.value} value={t.value}>
                              {t.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-canopy-text mb-2">
                          Status
                        </label>
                        <select
                          value={editingVacation.status}
                          onChange={(e) =>
                            setEditingVacation((v) => ({
                              ...v,
                              status: e.target.value,
                            }))
                          }
                          className="w-full border border-canopy-border rounded-lg px-3 py-2 text-sm"
                        >
                          <option value="Pending">Pending</option>
                          <option value="Approved">Approved</option>
                          <option value="Rejected">Rejected</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-canopy-text mb-2">
                          Start Date
                        </label>
                        <input
                          type="date"
                          value={editingVacation.start}
                          onChange={(e) =>
                            setEditingVacation((v) => ({
                              ...v,
                              start: e.target.value,
                            }))
                          }
                          className="w-full border border-canopy-border rounded-lg px-3 py-2 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-canopy-text mb-2">
                          End Date
                        </label>
                        <input
                          type="date"
                          value={editingVacation.end}
                          onChange={(e) =>
                            setEditingVacation((v) => ({
                              ...v,
                              end: e.target.value,
                            }))
                          }
                          className="w-full border border-canopy-border rounded-lg px-3 py-2 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-canopy-text mb-2">
                          Days
                        </label>
                        <input
                          type="number"
                          min="0.5"
                          step="0.5"
                          value={editingVacation.days}
                          onChange={(e) =>
                            setEditingVacation((v) => ({
                              ...v,
                              days: e.target.value,
                            }))
                          }
                          className="w-full border border-canopy-border rounded-lg px-3 py-2 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-canopy-text mb-2">
                          Notes
                        </label>
                        <input
                          type="text"
                          value={editingVacation.notes ?? ""}
                          onChange={(e) =>
                            setEditingVacation((v) => ({
                              ...v,
                              notes: e.target.value,
                            }))
                          }
                          placeholder="Optional"
                          className="w-full border border-canopy-border rounded-lg px-3 py-2 text-sm"
                        />
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2 sm:justify-end mt-4">
                      <button
                        type="button"
                        onClick={() => setEditingVacation(null)}
                        className="px-4 py-2 text-sm font-semibold rounded-lg border border-canopy-border hover:bg-white transition"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleEditSave}
                        className="canopy-btn-primary"
                      >
                        Save Changes
                      </button>
                    </div>
                  </div>
                )}

                {deletingVacationId === vacation.id && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 space-y-2">
                    <p className="text-sm font-medium text-red-800">
                      Delete this vacation request? This action cannot be
                      undone.
                    </p>
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => setDeletingVacationId(null)}
                        className="px-3 py-1 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteConfirm(vacation)}
                        className="px-3 py-1 text-sm bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );
};

export default VacationsOverview;
